import React from 'react';
import { Button, Checkbox, Dialog, Group, Icon, Tooltip } from '@economic/taco';
import { t, Trans } from '@lingui/macro';
import { Link } from 'react-router-dom';
import { useField } from 'react-final-form';
import { Field } from '~/app/shared/components/Form';
import ExternalLink from '~/app/shared/components/ExternalLink';

interface Props {
    prevStep: () => void;
    nextStep: () => void;
}

const CompanyInformation = ({ prevStep, nextStep }: Props) => {
    const fieldCompanyDescription = useField('companyDescription');

    const onPrevClick = () => {
        prevStep();
    };

    const onNextClick = () => {
        fieldCompanyDescription.input.onBlur();

        if (fieldCompanyDescription.meta.invalid) {
            return;
        }

        nextStep();
    };

    // true if string contains 5 or more words
    const validateProductDescription = (value: string) =>
        /^(\S+\s){4}\S+.*$/g.test(value) ? undefined : t`Describe what your company sells in at least 5 words`;

    return (
        <>
            <Dialog.Title>
                <Trans>Verify the company information</Trans>
            </Dialog.Title>

            <div className="flex mt-4">
                <span>
                    <Trans>Pensopay needs to know what your business sells in order to approve you.</Trans>
                </span>
            </div>

            <div className="flex mt-6">
                <Field.Text fieldClassName="w-full" label={t`CVR number`} name="companyCvr" readOnly />
            </div>

            <div className="flex mt-2 justify-end">
                <Link to="/settings/company/master-information">
                    <Trans>Update the master information</Trans> <Icon className="text-xs" name="undock" />
                </Link>
            </div>

            <div className="flex mt-1">
                <Field.Text fieldClassName="w-full" label={t`Company name`} name="companyName" readOnly />
            </div>

            <div className="flex mt-6">
                <span className="font-bold text-xs">
                    <Trans>What does your company sell?</Trans> *
                </span>
                <Tooltip
                    title={t`In order for pensopay to approve your redemption agreement application, they need to know what you sell in your business`}>
                    <Icon name="info" className="yt-blue-solid ml-2 rounded-full text-white !h-4 !w-4 !p-0 !ml-1" />
                </Tooltip>
            </div>

            <div className="flex-col mt-2">
                <Group>
                    <label className="mr-1">
                        <Field.Checkbox fieldClassName="flex" name="businessType.physicalProduct">
                            {({ input: { name, value, onChange } }) => (
                                <>
                                    <Checkbox className="!mt-0 mr-1" name={name} value={value} onChange={onChange} />
                                    <Trans>Physical products</Trans>
                                </>
                            )}
                        </Field.Checkbox>
                    </label>
                    <label className="mr-1">
                        <Field.Checkbox fieldClassName="flex" name="businessType.digitalSoftware">
                            {({ input: { name, value, onChange } }) => (
                                <>
                                    <Checkbox className="!mt-0 mr-1" name={name} value={value} onChange={onChange} />
                                    <Trans>Digital software</Trans>
                                </>
                            )}
                        </Field.Checkbox>
                    </label>
                    <label className="mr-1">
                        <Field.Checkbox fieldClassName="flex" name="businessType.services">
                            {({ input: { name, value, onChange } }) => (
                                <>
                                    <Checkbox className="!mt-0 mr-1" name={name} value={value} onChange={onChange} />
                                    <Trans>Services</Trans>
                                </>
                            )}
                        </Field.Checkbox>
                    </label>
                </Group>
            </div>

            <div className="mt-6">
                <Field.TextArea
                    required
                    placeholder={t`Describe specifically what you sell or offer in terms of services. Example: My company offers cleaning services.`}
                    name="companyDescription"
                    validate={validateProductDescription}
                />
            </div>

            <div className="flex mt-2">
                <span className="text-xs">
                    <Trans>Some business types can’t be approved by pensopay - see the</Trans>{' '}
                    <ExternalLink href="#pensopay-approved-businesses">
                        <Trans>list</Trans>
                    </ExternalLink>
                </span>
            </div>

            <Group className="flex mt-6 justify-end">
                <Button onClick={onPrevClick}>
                    <Trans>Back</Trans>
                </Button>
                <Button appearance="primary" onClick={onNextClick}>
                    <Trans>Next</Trans>
                </Button>
            </Group>
        </>
    );
};

export default CompanyInformation;
